import { isLeavingPage } from '@/lib/page-lifecycle/page-lifecycle';
import type { ActivityEntry, ActivitySource } from '@/lib/widget-activity';
import { activityTimeline } from './activity-timeline';
import { buildLogsRequest, SEVERITY_ERROR, type Attributes } from './otlp';
import {
  buildVersion,
  environmentName,
  networkInformation,
  normalizeFailure,
  parseRemoteEntry,
  portalId,
  truncate,
  type WidgetLoadFailure
} from './report';

/** Same-origin path; nginx in the pod forwards it to the SigNoz agent (docker/nginx.conf). */
export const DEFAULT_ENDPOINT = '/widget-load-failures';

/** The event the design names for this signal (design §4.5 / G5). */
export const EVENT_NAME = 'widget_remote_load_failed';

/** Distinct failures one page load may report; anything past this is dropped. */
export const DEFAULT_MAX_REPORTS = 10;

/** The first line of every record's body; the activity timeline follows it (PSDK-6558). */
export const BODY_HEADLINE = 'widget load failed';

export type Send = (endpoint: string, body: string) => boolean;

export interface WidgetLoadReporterOptions {
  endpoint?: string;
  serviceName?: string;
  maxReports?: number;
  now?: () => number;
  send?: Send;
  /** Whether the page is navigating away; defaults to `isLeavingPage`. */
  isLeaving?: () => boolean;
  /** Where the page's recent activity comes from; without it a failure travels alone (PSDK-6558). */
  activity?: ActivitySource;
}

/** Fits `SharedHostGateway`'s `Logger`. */
export interface WidgetLoadReporter {
  error(...args: unknown[]): void;
}

/**
 * `sendBeacon` first: it survives the navigation a user makes right after seeing a broken
 * widget, and a same-origin POST needs no preflight. `fetch` with `keepalive` when the
 * beacon queue refuses the payload. Responses are never read; nginx answers 204 when
 * reporting is switched off and the browser has nothing to do either way.
 */
export function sendSameOrigin(endpoint: string, body: string): boolean {
  const blob = new Blob([body], { type: 'application/json' });
  if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(endpoint, blob)) {
    return true;
  }
  if (typeof fetch === 'function') {
    void fetch(endpoint, {
      method: 'POST',
      body: blob,
      keepalive: true,
      credentials: 'omit'
    }).catch(() => undefined);
    return true;
  }
  return false;
}

/** Accepts and drops: the transport of a build whose telemetry must not leave the machine. */
export const sendNowhere: Send = () => true;

/**
 * The transport a reporter or counter uses unless given one: nothing leaves a local build
 * (`deployment.environment.name = local`, a developer's `npm start`). Local traffic is not
 * collected by decision, and the dev server has no endpoint for it anyway.
 */
export function defaultTransport(environment: string = environmentName()): Send {
  return environment === 'local' ? sendNowhere : sendSameOrigin;
}

/** The static resource both the log record and the counter travel under. */
export function resourceAttributes(serviceName: string): Attributes {
  return {
    'service.name': serviceName,
    'service.namespace': 'cockpit',
    'service.version': buildVersion(),
    'deployment.environment.name': environmentName()
  };
}

/**
 * Attribute names follow the design's event table (`widget.remote_name`,
 * `widget.remote_url`, `exception.message`) plus the semconv `exception.type` /
 * `exception.stacktrace`, so a `ChunkLoadError` (a load failure) can be told apart from a
 * `TypeError` thrown while the widget rendered: the same error boundary reports both.
 * `widget.load.elapsed_ms` and `network.connection.*` tell a slow network from a CDN that
 * answered at once with an error (TFLS-3279). `widget.activity.count` says how many lines
 * of the run-up the body carries (PSDK-6558). No token, no user id, no query string
 * anywhere (urls are cut to origin + path, inside messages and stack traces too): the
 * record identifies a widget and a portal.
 */
function recordAttributes(failure: WidgetLoadFailure, activityCount: number): Attributes {
  const remote = parseRemoteEntry(failure.url);
  return {
    'event.name': EVENT_NAME,
    'widget.remote_name': failure.scope,
    'widget.remote_url': failure.url,
    'widget.id': remote.widgetId,
    'widget.version': remote.version,
    'widget.instance_id': failure.instanceId,
    'exception.type': failure.type,
    'exception.message': truncate(failure.message),
    'exception.stacktrace': truncate(failure.stack),
    'widget.load.elapsed_ms': failure.elapsedMs,
    'widget.activity.count': activityCount,
    'portal.id': portalId(),
    'portal.origin': window.location.origin,
    'url.path': window.location.pathname,
    'user_agent.original': navigator.userAgent,
    'browser.online': navigator.onLine,
    ...networkInformation()
  };
}

/** The headline, then the timeline when there is one. */
export function recordBody(timeline: string): string {
  return timeline === '' ? BODY_HEADLINE : `${BODY_HEADLINE}\n${timeline}`;
}

/**
 * One OTel log record per distinct widget load failure on this page load, with the
 * page's recent activity in its body when a source is bound (PSDK-6558).
 *
 * Deduplicated by scope + url: a dashboard mounts the same widget many times, and the
 * module registry lets a remount retry a failed remote, so without this one outage would
 * fan out into a record per instance. Nothing while the page is leaving: a navigation
 * cancels the remotes still loading, and WebKit and Firefox report each of them as a
 * script error at that very moment, which is not a failure anyone sees (TFLS-3279). Never
 * throws; reporting must not take a widget down with it.
 */
export function createWidgetLoadReporter(
  options: WidgetLoadReporterOptions = {}
): WidgetLoadReporter {
  const {
    endpoint = DEFAULT_ENDPOINT,
    serviceName = 'wlabel',
    maxReports = DEFAULT_MAX_REPORTS,
    now = Date.now,
    send = defaultTransport(),
    isLeaving = isLeavingPage,
    activity
  } = options;
  const reported = new Set<string>();

  /** The snapshot for a failure; none when the source misbehaves, the failure travels anyway. */
  const snapshotFor = (failure: WidgetLoadFailure, time: number): ActivityEntry[] => {
    try {
      return (
        activity?.snapshotFor({ scope: failure.scope, instanceId: failure.instanceId }, time) ?? []
      );
    } catch {
      return [];
    }
  };

  return {
    error(...args: unknown[]) {
      try {
        const failure = normalizeFailure(args);
        if (!failure || isLeaving()) {
          return;
        }
        const key = `${failure.scope}\n${failure.url}`;
        if (reported.has(key) || reported.size >= maxReports) {
          return;
        }
        reported.add(key);

        const time = now();
        const snapshot = snapshotFor(failure, time);
        const request = buildLogsRequest(resourceAttributes(serviceName), [
          {
            timestamp: time,
            severityNumber: SEVERITY_ERROR,
            severityText: 'ERROR',
            body: recordBody(activityTimeline(snapshot, time)),
            attributes: recordAttributes(failure, snapshot.length)
          }
        ]);
        send(endpoint, JSON.stringify(request));
      } catch {
        // Deliberately silent: there is no better place to report a failed report.
      }
    }
  };
}
